import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { clubSettingsAPI } from '../services/api';
import { applyPrimaryColors } from '../utils/colorPalette';
import { BACKEND_BASE_URL } from '../services/api';
import type { ClubSettings } from '../types/clubSettings';

interface ClubSettingsContextType {
  settings: ClubSettings | null;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

const ClubSettingsContext = createContext<ClubSettingsContextType>({
  settings: null,
  loading: true,
  refreshSettings: async () => {},
});

export const useClubSettings = () => useContext(ClubSettingsContext);

/**
 * A böngészőfül ikonját, az iOS kezdőképernyő-ikont és a böngésző színsávját
 * a klub feltöltött címeréhez és színéhez igazítja.
 *
 * Az Android telepítéskori ikonja NEM innen jön: azt a Chrome kizárólag a
 * manifestből veszi, amit külön kéréssel tölt le - lásd api/manifest.js.
 */
const applyClubBranding = (settings: ClubSettings) => {
  const setLink = (rel: string, href: string) => {
    let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = href;
  };

  if (settings.crestUrl) {
    const crest = `${BACKEND_BASE_URL}${settings.crestUrl}`;
    setLink('icon', crest);
    setLink('apple-touch-icon', crest);
  }

  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeColor && settings.primaryColor) {
    themeColor.content = settings.primaryColor;
  }

  if (settings.clubName) {
    const appleTitle = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.content = settings.clubName;
  }
};

export const ClubSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<ClubSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    try {
      const data = await clubSettingsAPI.getSettings();
      setSettings(data);
      applyPrimaryColors(data.primaryColor);
      applyClubBranding(data);
    } catch {
      // Use defaults if API is unavailable
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const refreshSettings = useCallback(async () => {
    try {
      const data = await clubSettingsAPI.getSettings();
      setSettings(data);
      applyPrimaryColors(data.primaryColor);
      applyClubBranding(data);
    } catch {
      // ignore
    }
  }, []);

  return (
    <ClubSettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </ClubSettingsContext.Provider>
  );
};
